<template>
  <div
    class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[25px] mb-[25px]"
  >
    <div v-if="loading" class="col-span-full text-center py-8">
      <div class="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
      <p class="mt-4 text-gray-600 dark:text-gray-300">{{ directT('ecommerce.productsGrid.loadingProducts') }}</p>
    </div>
    
    <div v-else-if="products.length === 0" class="col-span-full text-center py-8">
      <p class="text-gray-600 dark:text-gray-300">{{ directT('ecommerce.productsGrid.noProductsFound') }}</p>
    </div>
    
    <div
      class="md:mb-[10px] lg:mb-[17px] bg-white dark:bg-[#0c1427] p-5 rounded-xl shadow-sm cursor-pointer"
      v-for="product in products"
      :key="product.id"
      v-else
      @click="navigateToEdit(product.id)"
    >
      <div class="relative">
        <span
          class="block ltr:right-0 rtl:left-0 bottom-0 w-[65px] h-[65px] absolute ltr:rounded-tl-md rtl:rounded-tr-md bg-white dark:bg-[#0c1427]"
        ></span>
        <div class="block rounded-md aspect-square">
          <img :src="product.image_link" :alt="product.title" class="rounded-md w-full h-full object-cover" />
        </div>
        <a
          :href="product.product_link"
          target="_blank"
          class="rounded-md transition-all z-[1] inline-flex items-center justify-center absolute ltr:right-0 rtl:left-0 bottom-0 w-[60px] h-[60px] bg-primary-500 text-white hover:bg-primary-400"
          @click.stop
        >
          <i class="material-symbols-outlined"> shopping_cart </i>
        </a>
      </div>
      <div class="mt-[19px]">
        <h6 class="text-md font-normal">
          <span class="transition-all hover:text-primary-500">
            {{ product.title }}
          </span>
        </h6>
        <div
          class="flex items-center justify-between mt-[12px] md:mt-[20px] lg:mt-[28px]"
        >
          <div class="flex items-center">
            <span
              class="block font-bold text-black dark:text-white text-[20px]"
            >
              {{ formatPrice(product.compare_at_price || product.price) }}
            </span>
            <span v-if="product.compare_at_price" class="block text-md text-gray-400 ltr:ml-[7px] rtl:mr-[7px]">
              {{ formatPrice(product.price) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="products.length > 0"
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div class="trezo-card-content">
      <Pagination 
        :items="pageSize" 
        :total="totalProducts" 
        :current="currentPage"
        @page-change="handlePageChange"
        class="pt-[0] md:pt-[0]" 
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from "vue";
import Pagination from "@/components/Common/Pagination.vue";
import { formatCurrency, getUserCurrency, getUserCurrencySymbol } from "@/utils/currency";
import { supabase } from "@/lib/supabaseClient";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

interface Product {
  id: string;
  title: string;
  image_link: string;
  price: number;
  compare_at_price: number | null;
  product_link: string;
  org_id: string;
}

export default defineComponent({
  name: "ProductsGrid",
  components: {
    Pagination,
  },
  setup() {
    const router = useRouter();
    const products = ref<Product[]>([]);
    const loading = ref(true);
    const error = ref<string | null>(null);
    const currentPage = ref(1);
    const pageSize = ref(9);
    const totalProducts = ref(0);
    const currencyCode = ref('ILS'); // ברירת מחדל
    const organizationId = ref<string | null>(null);

    // i18n setup
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };

    // Get the current user's organization ID
    const getUserOrganizationId = async (): Promise<string | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', user.id)
          .single();

        return profileData?.org_id as string || null;
      } catch (err) {
        console.error('Error fetching user organization:', err);
        return null;
      }
    };

    // Fetch products from the database
    const fetchProducts = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        if (!organizationId.value) {
          organizationId.value = await getUserOrganizationId();
        }
        
        if (!organizationId.value) {
          throw new Error("Could not determine organization ID");
        }
        
        console.log("Fetching products for organization:", organizationId.value);
        
        // Count total products for pagination
        const { count, error: countError } = await supabase
          .from('products')
          .select('*', { count: 'exact', head: true })
          .eq('org_id', organizationId.value);
          
        if (countError) {
          console.error("Error counting products:", countError);
          throw countError;
        }
        
        console.log("Total products found:", count);
        totalProducts.value = count || 0;
        
        // Fetch products for current page
        const from = (currentPage.value - 1) * pageSize.value;
        const to = from + pageSize.value - 1;
        
        console.log(`Fetching products from index ${from} to ${to}`);
        
        const { data, error: fetchError } = await supabase
          .from('products')
          .select('*')
          .eq('org_id', organizationId.value)
          .order('created_at', { ascending: false })
          .range(from, to);
          
        if (fetchError) {
          console.error("Error fetching products:", fetchError);
          throw fetchError;
        }
        
        console.log("Products data received:", data);
        
        // Type safety conversion
        const typedData = data || [];
        
        if (typedData.length === 0) {
          console.log("No products found for this organization, checking for any products...");
          
          // Check if there are any products at all in the database
          const { data: anyProducts, error: anyError } = await supabase
            .from('products')
            .select('id, org_id')
            .limit(5);
            
          if (anyError) {
            console.error("Error checking for any products:", anyError);
          } else {
            console.log("Sample products in database:", anyProducts);
          }
        }
        
        products.value = typedData.map(item => ({
          id: String(item.id || ''),
          title: String(item.title || ''),
          image_link: String(item.image_link || ''),
          price: Number(item.price || 0),
          compare_at_price: item.compare_at_price ? Number(item.compare_at_price) : null,
          product_link: String(item.product_link || ''),
          org_id: String(item.org_id || '')
        }));
        
        console.log("Processed products:", products.value);
      } catch (err) {
        console.error('Error fetching products:', err);
        error.value = 'Failed to load products. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    // Handle pagination
    const handlePageChange = (page: number) => {
      currentPage.value = page;
    };
    
    // Watch for page changes to reload products
    watch(currentPage, () => {
      fetchProducts();
    });

    // Initialize
    onMounted(async () => {
      try {
        // Load currency
        currencyCode.value = await getUserCurrency();
        
        // Fetch products
        await fetchProducts();
      } catch (error) {
        console.error('Error in component initialization:', error);
      }
    });

    const formatPrice = (price: number) => {
      return formatCurrency(price, currencyCode.value);
    };

    const navigateToEdit = (productId: string) => {
      router.push(`/ecommerce/edit-product?id=${productId}&bypass_auth=true`);
    };

    return {
      products,
      loading,
      error,
      currentPage,
      pageSize,
      totalProducts,
      formatPrice,
      handlePageChange,
      navigateToEdit,
      directT
    };
  },
});
</script>

<style lang="scss" scoped>
@media (min-width: 768px) {
  .md\:pt-\[0\] {
    padding-top: 0 !important;
  }
}
.pt-\[0\] {
  padding-top: 0 !important;
}
</style>
